/**
 * Copyright (C) Mairistem
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 *
 * Proprietary and confidential
 */

import type { ComponentType } from 'react';

import type { Mention, Registre, TypeActe } from '@/types';
import { Steps } from '@/types';
import {
  validationStep1Naissance,
  validationStep1Reconnaissance,
  validationStepEnfantReconnaissance,
  validationStep2,
  validationStep3,
  validationStep1Deces,
  validationStepDefunt,
  validationStepDeclarant,
  validationStep1Mariage,
  validationStepConjoint,
  validationStepParentsMariage,
} from '@/utils/validations';

import {
  StepActeDeces,
  StepActeMariage,
  StepActeNaissance,
  StepActeReconnaissance,
} from './StepActe';
import { StepConjoint1, StepConjoint2 } from './StepConjoint';
import {
  StepParentsConjoint1,
  StepParentsConjoint2,
} from './StepParentsMariage';
import { StepDefunt } from './StepDefunt';
import { StepDeclarant } from './StepDeclarant';
import { StepEnfant } from './StepEnfant';
import { StepParents } from './StepParents';
import { StepMentions } from './StepMentions';

/** Contexte de validation transmis à chaque validator du registre. */
interface ValidationContext {
  data: Record<string, any>;
  selectedRegistre?: Partial<Registre>;
  mentions: Mention[];
}

interface AssistantConfig {
  /** Composant à monter pour chaque étape de ce type d'acte. */
  components: Partial<Record<Steps, ComponentType>>;
  /** Validator de chaque étape de ce type d'acte ; signature commune, adaptation faite ici. */
  validators: Partial<
    Record<Steps, (ctx: ValidationContext) => Record<string, any>>
  >;
  /** Nom affiché dans la modale de fin de saisie (ex. « Toto Test » → nom réel de l'enfant). */
  getNomActe: (data: Record<string, any>) => string;
}

/**
 * Nom de l'enfant (commun aux actes de naissance et de reconnaissance :
 * mêmes champs `nomFamille`/`prenom` à plat).
 */
const getNomEnfant = (data: Record<string, any>) =>
  `${data?.prenom ?? ''} ${data?.nomFamille ?? ''}`.trim();

/**
 * Registre `TypeActe → configuration de l'assistant`, qui remplace les 4
 * points de couplage historiques d'`Assistant.tsx` à la naissance
 * (`useCreateActeNaissance`, le `switch` des validators, le `switch` des
 * composants d'étape, `typeActe="naissance" nomActe="Toto Test"` en dur).
 *
 * Colocalisé dans `components/Assistant` (et non `src/constants`) pour éviter
 * un cycle d'imports `constants → components → constants`.
 *
 * ⚠️ Volontairement **sans hook** dans ce registre : `config.useCreateActe()`
 * violerait la règle des hooks dès que `selectedTypeActe` change sur un
 * `Assistant` déjà monté (le provider réagit à `location.search`,
 * `components/Context/AssistantContextProvider.tsx`). La création d'acte
 * passe donc par un hook générique unique, `useCreateActe`, appelé une seule
 * fois dans `Assistant.tsx` et paramétré par `typeActe` à l'appel.
 */
const ASSISTANT_CONFIG: Partial<Record<TypeActe, AssistantConfig>> = {
  naissance: {
    components: {
      [Steps.Acte]: StepActeNaissance,
      [Steps.Parents]: StepParents,
      [Steps.Mentions]: StepMentions,
    },
    validators: {
      [Steps.Acte]: ({ data, selectedRegistre }) =>
        validationStep1Naissance(data, selectedRegistre),
      [Steps.Parents]: ({ data }) => validationStep2(data),
      [Steps.Mentions]: ({ mentions }) => validationStep3(mentions),
    },
    getNomActe: getNomEnfant,
  },
  reconnaissance: {
    components: {
      [Steps.Acte]: StepActeReconnaissance,
      [Steps.Enfant]: StepEnfant,
      [Steps.Parents]: StepParents,
      [Steps.Mentions]: StepMentions,
    },
    validators: {
      [Steps.Acte]: ({ data, selectedRegistre }) =>
        validationStep1Reconnaissance(data, selectedRegistre),
      [Steps.Enfant]: ({ data }) => validationStepEnfantReconnaissance(data),
      [Steps.Parents]: ({ data }) => validationStep2(data),
      [Steps.Mentions]: ({ mentions }) => validationStep3(mentions),
    },
    getNomActe: getNomEnfant,
  },
  deces: {
    components: {
      [Steps.Acte]: StepActeDeces,
      [Steps.Defunt]: StepDefunt,
      [Steps.Parents]: StepParents,
      [Steps.Declarant]: StepDeclarant,
      [Steps.Mentions]: StepMentions,
    },
    validators: {
      [Steps.Acte]: ({ data, selectedRegistre }) =>
        validationStep1Deces(data, selectedRegistre),
      [Steps.Defunt]: ({ data }) => validationStepDefunt(data),
      [Steps.Parents]: ({ data }) => validationStep2(data),
      [Steps.Declarant]: ({ data }) => validationStepDeclarant(data),
      [Steps.Mentions]: ({ mentions }) => validationStep3(mentions),
    },
    // Le défunt porte le même état civil à plat que l'enfant (`nomFamille`,
    // `prenom`) : le même extracteur convient.
    getNomActe: getNomEnfant,
  },
  mariage: {
    components: {
      [Steps.Acte]: StepActeMariage,
      [Steps.Conjoint1]: StepConjoint1,
      [Steps.ParentsConjoint1]: StepParentsConjoint1,
      [Steps.Conjoint2]: StepConjoint2,
      [Steps.ParentsConjoint2]: StepParentsConjoint2,
      [Steps.Mentions]: StepMentions,
    },
    validators: {
      [Steps.Acte]: ({ data, selectedRegistre }) =>
        validationStep1Mariage(data, selectedRegistre),
      [Steps.Conjoint1]: ({ data }) =>
        validationStepConjoint(data?.conjoint1, data?.dateMariage),
      [Steps.ParentsConjoint1]: ({ data }) =>
        validationStepParentsMariage(data, 1),
      [Steps.Conjoint2]: ({ data }) =>
        validationStepConjoint(data?.conjoint2, data?.dateMariage),
      [Steps.ParentsConjoint2]: ({ data }) =>
        validationStepParentsMariage(data, 2),
      [Steps.Mentions]: ({ mentions }) => validationStep3(mentions),
    },
    /*
     * Deux individus principaux : la modale de fin les nomme tous les deux, sans
     * quoi elle ne dirait de quel acte il s'agit qu'à moitié.
     */
    getNomActe: (data) =>
      [
        `${data?.conjoint1?.prenom ?? ''} ${data?.conjoint1?.nomFamille ?? ''}`.trim(),
        `${data?.conjoint2?.prenom ?? ''} ${data?.conjoint2?.nomFamille ?? ''}`.trim(),
      ]
        .filter(Boolean)
        .join(' et '),
  },
};

export default ASSISTANT_CONFIG;
export type { AssistantConfig, ValidationContext };
